import { Battery, Sun, Zap, AlertTriangle, Activity, Server, X, LucideIcon } from 'lucide-react';
import { KPICard } from '../KPICard';
import { PortfolioAggregateData } from '../../types/portfolio.types';

interface PortfolioSummaryPanelProps {
  data: PortfolioAggregateData;
  isLoading: boolean;
  onClose?: () => void;
}

export function PortfolioSummaryPanel({ data, isLoading, onClose }: PortfolioSummaryPanelProps) {
  if (isLoading) return <SummaryPanelSkeleton />;

  const { fleet_overview, battery_storage_fleet, solar_pv_fleet, ev_charging_fleet, alarm_summary } = data;

  return (
    <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-5 space-y-5 sticky top-0">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-ems-text-primary">Portfolio Summary</h2>
          <p className="text-xs text-ems-text-muted mt-0.5">
            {data.filter_applied.portfolio_name || 'All Portfolios'} &middot; {data.site_count} sites
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-ems-text-muted hover:text-ems-text-secondary hover:bg-ems-bg-card-hover transition-colors"
            aria-label="Close summary"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Fleet Overview */}
      <Section icon={Server} iconColor="text-ems-green" title="Fleet Overview">
        <div className="grid grid-cols-2 gap-2">
          <KPICard icon={Activity} label="Total Capacity" value={fleet_overview.total_capacity_mwh} unit="MWh" color="blue" size="sm" />
          <KPICard
            icon={Server}
            label="Online Sites"
            value={fleet_overview.online_sites}
            unit={`/ ${fleet_overview.total_sites}`}
            color={fleet_overview.online_sites / fleet_overview.total_sites > 0.9 ? 'green' : 'amber'}
            size="sm"
          />
        </div>
      </Section>

      {battery_storage_fleet && battery_storage_fleet.site_count > 0 && (
        <Section icon={Battery} iconColor="text-ems-green" title={`Battery Storage (${battery_storage_fleet.site_count})`}>
          <div className="grid grid-cols-2 gap-2">
            <KPICard icon={Battery} label="Avg SOC" value={battery_storage_fleet.avg_state_of_charge ?? 0} unit="%" color={(battery_storage_fleet.avg_state_of_charge ?? 0) > 50 ? 'green' : 'amber'} size="sm" />
            <KPICard icon={Activity} label="Avg SOH" value={battery_storage_fleet.avg_state_of_health ?? 0} unit="%" color={(battery_storage_fleet.avg_state_of_health ?? 0) > 90 ? 'green' : 'amber'} size="sm" />
            <KPICard icon={Zap} label="Capacity" value={battery_storage_fleet.total_capacity_mwh} unit="MWh" color="blue" size="sm" />
            <KPICard icon={Activity} label="Efficiency" value={battery_storage_fleet.avg_round_trip_efficiency ?? 0} unit="%" color={(battery_storage_fleet.avg_round_trip_efficiency ?? 0) > 85 ? 'green' : 'amber'} size="sm" />
          </div>
        </Section>
      )}

      {solar_pv_fleet && solar_pv_fleet.site_count > 0 && (
        <Section icon={Sun} iconColor="text-amber-500" title={`Solar PV (${solar_pv_fleet.site_count})`}>
          <div className="grid grid-cols-2 gap-2">
            <KPICard icon={Zap} label="Capacity" value={solar_pv_fleet.total_capacity_kw} unit="kW" color="blue" size="sm" />
            <KPICard icon={Activity} label="Perf Ratio" value={solar_pv_fleet.avg_performance_ratio ?? 0} unit="%" color={(solar_pv_fleet.avg_performance_ratio ?? 0) > 75 ? 'green' : 'amber'} size="sm" />
          </div>
        </Section>
      )}

      {ev_charging_fleet && ev_charging_fleet.site_count > 0 && (
        <Section icon={Zap} iconColor="text-ems-green" title={`EV Charging (${ev_charging_fleet.site_count})`}>
          <div className="grid grid-cols-2 gap-2">
            <KPICard icon={Zap} label="Stations" value={ev_charging_fleet.total_stations} unit="" color="blue" size="sm" />
            <KPICard icon={Activity} label="Utilization" value={ev_charging_fleet.avg_utilization_rate ?? 0} unit="%" color={(ev_charging_fleet.avg_utilization_rate ?? 0) > 60 ? 'green' : 'amber'} size="sm" />
          </div>
        </Section>
      )}

      {/* Alarms */}
      <Section icon={AlertTriangle} iconColor="text-status-degraded" title="Active Alarms">
        {alarm_summary.needs_attention === 0 ? (
          <p className="text-sm text-ems-text-muted text-center py-3">No active alarms</p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {alarm_summary.emergency_count > 0 && (
              <KPICard icon={AlertTriangle} label="Emergency" value={alarm_summary.emergency_count} unit="" color="red" size="sm" alert />
            )}
            {alarm_summary.critical_count > 0 && (
              <KPICard icon={AlertTriangle} label="Critical" value={alarm_summary.critical_count} unit="" color="red" size="sm" />
            )}
            {alarm_summary.warning_count > 0 && (
              <KPICard icon={AlertTriangle} label="Warning" value={alarm_summary.warning_count} unit="" color="amber" size="sm" />
            )}
            {alarm_summary.medium_count > 0 && (
              <KPICard icon={AlertTriangle} label="Medium" value={alarm_summary.medium_count} unit="" color="amber" size="sm" />
            )}
            {alarm_summary.low_count > 0 && (
              <KPICard icon={AlertTriangle} label="Low" value={alarm_summary.low_count} unit="" color="blue" size="sm" />
            )}
          </div>
        )}
      </Section>
    </div>
  );
}

function Section({ icon: Icon, iconColor, title, children }: { icon: LucideIcon; iconColor: string; title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold text-ems-text-primary flex items-center gap-1.5">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        {title}
      </h3>
      {children}
    </section>
  );
}

function SummaryPanelSkeleton() {
  return (
    <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-5 space-y-5 animate-pulse">
      <div className="h-6 bg-ems-bg rounded w-3/4" />
      <div className="space-y-2">
        <div className="h-5 bg-ems-bg rounded w-1/2" />
        <div className="grid grid-cols-2 gap-2">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-20 bg-ems-bg rounded" />)}
        </div>
      </div>
    </div>
  );
}
